import React from 'react';
import CodeBlock from './UI Components/CodeBlock';

const Docs = () => {
    return (
        <article id='docs' className="mt-32 p-4 px-[20vw] max-sm:px-[5vw]">
            <header>
                <h1 className="text-2xl font-bold text-start text-gray-900 mb-6">
                    Understanding CSS Priority Order
                </h1>
            </header>

            <section className="mb-6">
                <p className="text-lg text-gray-700 mb-4">
                    As a web developer, I’ve been there – a style not applying as expected, even though it *should* work. After some troubleshooting, I realized that the issue usually boils down to one thing: CSS priority order. Understanding CSS specificity and how the browser interprets which styles to apply can make all the difference in writing clean, efficient code. Let me walk you through it!
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    What is CSS Priority Order?
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                    CSS works by applying styles to HTML elements based on rules, selectors, and values you define. However, when there’s more than one rule for the same element, CSS uses a set of rules (called the <strong>CSS priority order</strong>) to decide which style wins.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                    If you’re struggling with conflicting styles in your project, then understanding how CSS prioritizes rules is key. The most specific rule will always take precedence.
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    Why Should You Care About CSS Specificity?
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                    You might have seen terms like "specificity" or "CSS rules" thrown around when discussing styles. Specificity refers to the weight or importance of a CSS rule. Think of it like a hierarchy – the more specific a selector is, the higher its priority. This impacts which styles get applied when multiple rules are in play.
                </p>
                <div className=" mt-12 mb-14">
                    <p className="text-lg text-gray-700 mb-4">
                        To give you an example:
                    </p>

                    <CodeBlock code={`    /* Less specific */
    p {
        color: red;
    }
    
    /* More specific */
    #special-paragraph {
        color: blue;
    }`} language="css" />
                </div>

                <p className="text-lg text-gray-700 mb-4">
                    In the case above, the text inside the <code>#special-paragraph</code> ID will be <strong>blue</strong>, because the ID selector (<code>#special-paragraph</code>) is more specific than the <code>p</code> element selector.
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    The Layers of CSS Priority: From Least to Most Specific
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                    CSS uses a priority system to decide which rule wins when there are conflicting styles. Let’s break down this order from lowest to highest specificity:
                </p>

                <ol className="list-decimal list-inside space-y-4 text-lg text-gray-700 flex flex-col gap-10 mt-10">
                    <li >
                        <span className="font-semibold">Inline Styles:</span> The highest priority goes to inline styles – that’s when you apply styles directly in the HTML element using the <code>style</code> attribute.
                        <CodeBlock code={`<p style="color: green;">This text is green!</p>`} language="css" />
                    </li>
                    <li>
                        <span className="font-semibold">IDs (<code>#selector</code>):</span> IDs are extremely specific. If you apply a style to an element using an ID, that rule will override a class or an element selector.
                        <CodeBlock code={`#main-heading {
  font-size: 24px;
}`} language="css" />
                    </li>
                    <li>
                        <span className="font-semibold">Classes (<code>.selector</code>):</span> Class selectors are more specific than element selectors, but less than IDs. They’re often used to group elements with similar styles.
                        <CodeBlock code={`.highlighted {
  color: orange;
}`} language="css" />
                    </li>
                    <li>
                        <span className="font-semibold">Element or Type Selectors (<code>element</code>):</span> These selectors are the least specific. They target HTML elements such as <code>div</code>, <code>p</code>, or <code>h1</code>.
                        <CodeBlock code={`p {
  font-family: Arial, sans-serif;
}`} language="css" />
                    </li>
                    <li>
                        <span className="font-semibold">Universal Selector (<code>*</code>):</span> This selector affects all elements. While it's powerful, it has the lowest priority in the cascade.
                        <CodeBlock code={`* {
  margin: 0;
  padding: 0;
}`} language="css" />
                    </li>
                </ol>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    The Power of <code>!important</code>
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                    In certain cases, you might want to force a style to be applied no matter what. This is where the <code>!important</code> rule comes in. It gives a style higher priority than any other.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                    However, use it sparingly – too much reliance on <code>!important</code> can make your stylesheets messy and hard to maintain.
                </p>
                <div className='my-7'>

                    <CodeBlock code={`h2 {
  color: purple !important;
  }`} language="css" />
                </div>
                <p className="text-lg text-gray-700 mb-4">
                    In this case, no other conflicting rules (including IDs, classes, or even inline styles) can override this purple color. But remember, **don’t abuse it**!
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    How to Handle Specificity and Avoid Overriding Confusion
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                    As a developer, the key is to balance specificity. If you use IDs excessively, it can lead to difficulties when you need to make changes. I prefer using classes and carefully selecting which selector types to apply.
                </p>
                <div className="my-7">

                    <CodeBlock code={`h1.main-header {
  font-size: 36px;
  font-weight: bold;
  }`} language="css" />
                </div>
                <p className="text-lg text-gray-700 mb-4">
                    By targeting a more specific combination, you keep things organized and avoid conflicts with other rules.
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-20">
                    Common Mistakes to Avoid
                </h2>
                <ul className="list-disc list-inside space-y-4 text-lg text-gray-700">
                    <li><strong>Overusing Inline Styles:</strong> Inline styles should be used minimally. They are tough to override and can lead to conflicting styles.</li>
                    <li><strong>Using Too Many IDs:</strong> IDs are powerful, but their specificity can make your code harder to maintain. Use them only when absolutely necessary.</li>
                    <li><strong>Neglecting to Organize Your CSS:</strong> Try to maintain a logical order in your stylesheet. Group similar selectors together to minimize confusion later on.</li>
                </ul>
            </section>
        </article>
    );
};

export default Docs;
